import { ReactNode, forwardRef, ButtonHTMLAttributes, HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * ATRIUM — Luminous Atrium design primitives
 * Single source of truth for marketing/long-form surfaces.
 * Cream paper bg, forest green accent, Instrument Sans, rounded-full / 2xl / 3xl,
 * soft layered shadows, glass cards, pill eyebrows, ambient gradients.
 */

// ─────────────────────────────────────────────
// Pill — eyebrow chip used above every hero / section
// ─────────────────────────────────────────────
interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: "default" | "dark";
  dot?: boolean;
}
export const Pill = forwardRef<HTMLSpanElement, PillProps>(
  ({ children, className, tone = "default", dot = true, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-2 rounded-full backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium",
        tone === "default" &&
          "border border-border/60 bg-background/70 text-foreground/70 shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_8px_24px_-12px_rgba(62,92,77,0.18)]",
        tone === "dark" && "border border-background/20 bg-background/10 text-background/80",
        className
      )}
      {...props}
    >
      {dot && (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            tone === "default" ? "bg-primary" : "bg-background/80"
          )}
        />
      )}
      {children}
    </span>
  )
);
Pill.displayName = "Pill";

// ─────────────────────────────────────────────
// AmbientBackdrop — radial cream/forest wash, behind heroes & CTAs
// ─────────────────────────────────────────────
interface AmbientBackdropProps {
  variant?: "hero" | "soft" | "dark";
  className?: string;
}
export const AmbientBackdrop = ({ variant = "hero", className }: AmbientBackdropProps) => {
  const styles: Record<string, string> = {
    hero:
      "radial-gradient(1100px 520px at 50% -10%, hsl(50 60% 92%) 0%, transparent 60%), radial-gradient(900px 480px at 85% 10%, hsl(150 30% 90%) 0%, transparent 65%)",
    soft:
      "radial-gradient(800px 360px at 20% 0%, hsl(50 60% 94%) 0%, transparent 60%), radial-gradient(700px 400px at 90% 100%, hsl(150 30% 92%) 0%, transparent 65%)",
    dark:
      "radial-gradient(500px 240px at 10% 0%, hsl(150 30% 30%) 0%, transparent 60%)",
  };
  return (
    <div
      aria-hidden
      className={cn("absolute inset-0 -z-10 pointer-events-none", className)}
      style={{ background: styles[variant] }}
    />
  );
};

// ─────────────────────────────────────────────
// SectionShell — consistent vertical rhythm + container
// ─────────────────────────────────────────────
interface SectionShellProps extends HTMLAttributes<HTMLElement> {
  size?: "sm" | "md" | "lg";
  bare?: boolean; // skip container if true
}
export const SectionShell = forwardRef<HTMLElement, SectionShellProps>(
  ({ children, className, size = "md", bare = false, ...props }, ref) => {
    const padY = {
      sm: "py-12 sm:py-16",
      md: "py-16 sm:py-20 md:py-28",
      lg: "py-20 sm:py-28 md:py-36",
    }[size];
    return (
      <section ref={ref} className={cn("relative", padY, className)} {...props}>
        {bare ? children : (
          <div className="container mx-auto px-4 md:px-6 lg:px-8">{children}</div>
        )}
      </section>
    );
  }
);
SectionShell.displayName = "SectionShell";

// ─────────────────────────────────────────────
// AtriumHero — eyebrow + headline (with optional italic accent) + intro
// ─────────────────────────────────────────────
interface AtriumHeroProps {
  eyebrow?: string;
  title: ReactNode;
  accent?: string;
  intro?: ReactNode;
  align?: "center" | "left";
  children?: ReactNode; // CTAs, etc.
  backdrop?: AmbientBackdropProps["variant"] | false;
  className?: string;
}
export const AtriumHero = ({
  eyebrow,
  title,
  accent,
  intro,
  align = "center",
  children,
  backdrop = "hero",
  className,
}: AtriumHeroProps) => (
  <section className={cn("relative overflow-hidden", className)}>
    {backdrop && <AmbientBackdrop variant={backdrop} />}
    <div className="container mx-auto px-4 md:px-6 lg:px-8 pt-20 sm:pt-28 pb-10 md:pb-14">
      <div
        className={cn(
          "max-w-4xl space-y-6",
          align === "center" ? "mx-auto text-center" : "text-left"
        )}
      >
        {eyebrow && (
          <div className={cn("flex", align === "center" ? "justify-center" : "justify-start")}>
            <Pill>{eyebrow}</Pill>
          </div>
        )}
        <h1 className="font-sans font-medium text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[1.02] text-foreground">
          {title}
          {accent && (
            <>
              {" "}
              <span className="font-serif italic font-light text-primary">{accent}</span>
            </>
          )}
        </h1>
        {intro && (
          <p
            className={cn(
              "text-base md:text-lg text-foreground/70 leading-relaxed",
              align === "center" ? "max-w-2xl mx-auto" : "max-w-2xl"
            )}
          >
            {intro}
          </p>
        )}
        {children && (
          <div
            className={cn(
              "flex flex-wrap gap-3 pt-2",
              align === "center" ? "justify-center" : "justify-start"
            )}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────
// Buttons — pill, ≥52px, forest shadow on hover
// ─────────────────────────────────────────────
type CTAProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: false;
  size?: "md" | "lg";
};

const ctaBase =
  "inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-[-0.005em] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50";

export const PrimaryCTA = forwardRef<HTMLButtonElement, CTAProps>(
  ({ className, size = "lg", children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        ctaBase,
        "bg-foreground text-background hover:bg-foreground/90 shadow-[0_10px_30px_-12px_rgba(62,92,77,0.45)] hover:shadow-[0_18px_42px_-14px_rgba(62,92,77,0.55)] hover:-translate-y-0.5",
        size === "lg" ? "h-[52px] px-7 text-[15px]" : "h-11 px-5 text-sm",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
PrimaryCTA.displayName = "PrimaryCTA";

export const SecondaryCTA = forwardRef<HTMLButtonElement, CTAProps>(
  ({ className, size = "lg", children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        ctaBase,
        "border border-border/60 bg-background/70 backdrop-blur text-foreground hover:bg-background hover:border-foreground/30 hover:-translate-y-0.5",
        size === "lg" ? "h-[52px] px-7 text-[15px]" : "h-11 px-5 text-sm",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
SecondaryCTA.displayName = "SecondaryCTA";

// ─────────────────────────────────────────────
// DarkCTAPanel — high-contrast forest band for final calls-to-action
// ─────────────────────────────────────────────
interface DarkCTAPanelProps {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
}
export const DarkCTAPanel = ({
  eyebrow,
  title,
  description,
  children,
  className,
}: DarkCTAPanelProps) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-3xl bg-foreground text-background p-8 sm:p-12 md:p-16",
      className
    )}
  >
    <AmbientBackdrop variant="dark" className="opacity-30" />
    <div className="relative max-w-2xl mx-auto text-center space-y-5">
      {eyebrow && (
        <div className="flex justify-center">
          <Pill tone="dark">{eyebrow}</Pill>
        </div>
      )}
      <h2 className="font-sans font-medium text-3xl sm:text-4xl md:text-5xl tracking-[-0.025em] leading-[1.05]">
        {title}
      </h2>
      {description && (
        <p className="text-background/75 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
          {description}
        </p>
      )}
      {children && (
        <div className="flex flex-wrap gap-3 justify-center pt-2">{children}</div>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// IconStamp — colored circle wrapper for lucide icons
// ─────────────────────────────────────────────
interface IconStampProps extends HTMLAttributes<HTMLDivElement> {
  tone?: "primary" | "info" | "warning" | "destructive" | "success" | "neutral";
  size?: "sm" | "md" | "lg";
}
export const IconStamp = forwardRef<HTMLDivElement, IconStampProps>(
  ({ children, className, tone = "primary", size = "md", ...props }, ref) => {
    const tones: Record<string, string> = {
      primary: "bg-primary/12 text-primary ring-primary/20",
      info: "bg-sky-500/12 text-sky-700 ring-sky-500/20",
      warning: "bg-amber-500/12 text-amber-700 ring-amber-500/20",
      destructive: "bg-destructive/12 text-destructive ring-destructive/20",
      success: "bg-emerald-500/12 text-emerald-700 ring-emerald-500/20",
      neutral: "bg-foreground/8 text-foreground/70 ring-foreground/15",
    };
    const sizes = {
      sm: "h-8 w-8 [&_svg]:h-4 [&_svg]:w-4",
      md: "h-11 w-11 [&_svg]:h-5 [&_svg]:w-5",
      lg: "h-14 w-14 [&_svg]:h-6 [&_svg]:w-6",
    };
    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center rounded-full ring-1 backdrop-blur-sm",
          tones[tone],
          sizes[size],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
IconStamp.displayName = "IconStamp";

// ─────────────────────────────────────────────
// GlassPanel — generic rounded-3xl glass surface
// ─────────────────────────────────────────────
interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  padding?: "sm" | "md" | "lg";
  hover?: boolean;
}
export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
  ({ children, className, padding = "md", hover = false, ...props }, ref) => {
    const pad = {
      sm: "p-5 md:p-6",
      md: "p-6 md:p-8",
      lg: "p-8 md:p-12",
    }[padding];
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-3xl border border-border/50 bg-card/85 backdrop-blur-sm",
          pad,
          hover &&
            "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_42px_-18px_rgba(62,92,77,0.25)] hover:border-border/70",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
GlassPanel.displayName = "GlassPanel";
